<template>
    <div id="upcoming-table">
        <!-- Loading state -->
        <div v-if="loading"
             class="text-center mt-4">
            <div class="spinner-border text-light"
                 role="status">
                <span class="sr-only">{{ $t('common.loading') }}...</span>
            </div>
        </div>

        <!-- Loop over activities and their warehouses -->
        <div v-else>
            <div v-for="activity in activities"
                 :key="activity.id"
                 class="activity-section mb-5">
                <!-- Loop over warehouses for this activity -->
                <div v-for="wId in activity.warehouses"
                     :key="wId"
                     class="warehouse-section mb-4">
                    <h5 class="mb-2">
                        {{ getWarehouseName(wId) }}
                    </h5>

                    <!-- Upcoming activities table for this warehouse -->
                    <div v-if="warehouseActivities[wId] && warehouseActivities[wId].length > 0"
                         class="table-responsive">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th width="250">
                                        {{ $t('common.date') }}
                                    </th>
                                    <th>{{ $t('common.time') }}</th>
                                    <th v-if="activities.length > 1">
                                        {{ $t('common.activity') }}
                                    </th>
                                    <th>{{ $t('common.location') }}</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="activityItem in warehouseActivities[wId]"
                                    :key="activityItem.id">
                                    <td>
                                        <div class="activity-date">
                                            <div class="date-day">
                                                {{ formatDate(activityItem.startDate) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="activity-time">
                                            <div class="time-start">
                                                {{ formatTime(activityItem.startDate) }} - {{ formatTime(activityItem.endDate) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td v-if="activities.length > 1">
                                        <strong>{{ activity.name }}</strong>
                                    </td>
                                    <td>{{ activityItem.locationName }}</td>
                                    <td class="text-right">
                                        <button v-if="activityItem.canBook"
                                                class="btn btn-sm btn-outline-success"
                                                @click="bookActivity(activityItem)">
                                            {{ $t('actions.reserve') }}
                                        </button>
                                        <span v-else-if="activityItem.reservationStartDate && activityItem.reservationStartDate > new Date()"
                                              class="badge">
                                            {{ $t('upcoming.reservation_not_open') }}
                                        </span>
                                        <span v-else-if="activityItem.reservationEndDate && activityItem.reservationEndDate < new Date()"
                                              class="badge">
                                            {{ $t('upcoming.reservation_closed') }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- No results for this warehouse -->
                    <div v-else
                         class="text-center mt-3">
                        <div class="alert alert-info">
                            <i class="fas fa-info-circle me-2" />
                            {{ $t('upcoming.no_upcoming') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ReservationModal v-if="selectedItem"
                          ref="reservationModal"
                          :item="selectedItem"
                          :csrf-token="csrfToken"
                          :has-siblings="false"
                          @close="handleModalClose"
                          @reserved="handleReservationComplete" />

        <add-to-cart-modal v-if="showAddedToCartModal"
                           @close="closeAddedToCartModal" />
    </div>
</template>

<script>
import axios from 'axios'
import { format, parseISO, addMonths, endOfMonth } from 'date-fns'
import nl from 'date-fns/locale/nl'
import enGB from 'date-fns/locale/en-GB'
import i18next from 'i18next'
import ReservationModal from '../time-table/ReservationModal.vue'
import AddToCartModal from '../cart/AddToCartModal.vue'
import { cartEventBus } from '../cart'

export default {
  name: 'UpcomingTableOverview',

  components: {
    ReservationModal,
    AddToCartModal
  },

  data () {
    return {
      loading: true,
      csrfToken: this.$parent.csrfToken,
      activities: this.$parent.activities || [],
      warehouses: this.$parent.warehouses || [],
      perPage: this.$parent.perPage,
      selectedItem: null,
      showAddedToCartModal: false,
      warehouseActivities: {},
      i18next,
      locales: {
        nl,
        en: enGB
      }
    }
  },

  async mounted () {
    await this.loadUpcomingActivities()
  },

  methods: {
    async loadUpcomingActivities () {
      this.loading = true

      try {
        // Clear previous data
        this.warehouseActivities = {}

        for (const activity of this.activities) {
          for (const warehouseId of activity.warehouses) {
            this.warehouseActivities[warehouseId] = []
            await this.loadWarehouseActivities(activity.id, warehouseId)
          }
        }
      } catch (error) {
        console.error('Error loading upcoming activities:', error) // eslint-disable-line no-console
      } finally {
        this.loading = false
      }
    },

    async loadWarehouseActivities (activityId, warehouseId) {
      try {
        const startDate = new Date()
        const endDate = endOfMonth(addMonths(startDate, 7))

        const params = {
          function: 'dataduiker/fcSubscriptionAgenda',
          Timetable: true,
          StartDate: format(startDate, 'yyyy-MM-dd'),
          EndDate: format(endDate, 'yyyy-MM-dd'),
          per_page: this.perPage,
          page: 1,
          ActivityID: [activityId],
          WarehouseID: [warehouseId]
        }

        const response = await axios.get('/php/xhr/ajax.php', { params })

        if (response.data && response.data.items) {
          // Transform the timetable data to upcoming activities format
          const transformed = this.transformTimetableData(response.data.items)
          this.warehouseActivities[warehouseId] = transformed
        } else {
          this.warehouseActivities[warehouseId] = []
        }
      } catch (error) {
        console.error(`Error loading activities for activity ${activityId}, warehouse ${warehouseId}:`, error) // eslint-disable-line no-console
        this.warehouseActivities[warehouseId] = []
      }
    },

    formatDate (dateString, part) {
      try {
        const date = parseISO(dateString)
        const locale = this.locales[i18next.language] || nl

        switch (part) {
          case 'day':
            return format(date, 'd', { locale })
          case 'month':
            return format(date, 'MMM', { locale })
          case 'year':
            return format(date, 'yyyy', { locale })
          default: {
            const formattedDate = format(date, 'EEEE d MMMM yyyy', { locale })
            // Capitalize first letter (vrijdag > Vrijdag)
            return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
          }
        }
      } catch (error) {
        return dateString
      }
    },

    formatTime (dateString) {
      try {
        const date = parseISO(dateString)
        return format(date, 'HH:mm')
      } catch (error) {
        return dateString
      }
    },

    getWarehouseName (warehouseId) {
      const warehouse = this.warehouses.find(w => w.id === warehouseId)
      return warehouse ? warehouse.name : `Accommodatie ${warehouseId}`
    },

    transformTimetableData (items) {
      const transformed = []
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day

      if (!items || !Array.isArray(items)) return transformed

      items.forEach(item => {
        if (item.start && item.end && item.extendedProps?.Type !== 'CLOSURE') {
          // Check if reservation is currently open
          const reservationStart = item.extendedProps?.ReservationStartDate ? new Date(item.extendedProps.ReservationStartDate) : null
          const reservationEnd = item.extendedProps?.ReservationEndDate ? new Date(item.extendedProps.ReservationEndDate) : null

          let canBook = true

          // Check reservation window
          if (reservationStart && reservationEnd) {
            canBook = reservationStart <= today && reservationEnd >= today
          }

          // Additional checks for fully booked or cancelled
          if (
            (
              Number(item.extendedProps?.CurrentEntryQuantity) &&
                Number(item.extendedProps?.MaxEntryQuantity) &&
                Number(item.extendedProps.CurrentEntryQuantity) >= Number(item.extendedProps.MaxEntryQuantity)
            ) ||
            item.extendedProps?.CancelYN
          ) {
            canBook = false
          }

          // Find the corresponding activity to get warehouse information
          const activity = this.activities.find(a => a.id === item.extendedProps?.ActivityID)

          transformed.push({
            id: item.id,
            startDate: item.start,
            endDate: item.end,
            name: item.extendedProps?.Name || 'Activiteit',
            locationName: item.extendedProps?.Location?.Name || null,
            warehouses: activity ? activity.warehouses : [],
            subscriptionAgendaId: item.extendedProps?.SubscriptionAgendaID || null,
            canBook,
            reservationStartDate: reservationStart,
            reservationEndDate: reservationEnd,
            // Include all extendedProps for the modal
            extendedProps: {
              ...item.extendedProps,
              Name: item.extendedProps?.Name || 'Activiteit',
              'Location.Name': item.extendedProps?.Location?.Name || null,
              'Warehouse.Name': item.extendedProps?.['Warehouse.Name'] || null
            }
          })
        }
      })

      // Sort by start date
      return transformed.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    },

    bookActivity (activity) {
      // Use the activity directly since it already has the right structure
      this.selectedItem = {
        id: activity.id,
        start: activity.startDate,
        end: activity.endDate,
        originalEnd: activity.endDate,
        extendedProps: activity.extendedProps
      }

      this.$nextTick(() => {
        this.$refs.reservationModal.fetchReservationOptions()
      })
    },

    handleModalClose () {
      this.selectedItem = null
    },

    handleReservationComplete () {
      this.showAddedToCartModal = true
      this.loadUpcomingActivities() // Refresh the data

      // Trigger cart modal to show
      cartEventBus.$emit('show-cart-modal', {
        hasIncassoProcess: false
      })
    },

    closeAddedToCartModal () {
      this.showAddedToCartModal = false
    }
  }
}
</script>
